import { Icon } from "@/components/icon/Icon";

/**
 * 단가 탭 하단의 MCP 사용 안내. 접혀 있는 정적 문서라 `<details>` 로만 만들어요 —
 * 열림 상태·애니메이션을 직접 들고 있을 이유가 없어요.
 */

const TOOLS: { name: string; desc: string; write?: boolean }[] = [
  { name: "get_pricing_config", desc: "지금 서비스 중인 단가표 전체를 읽어요." },
  { name: "list_pricing_keys", desc: "패치에 쓸 수 있는 항목 키 목록이에요." },
  { name: "list_pricing_revisions", desc: "발행 이력을 최신순으로 봐요." },
  { name: "get_pricing_revision", desc: "특정 버전의 내용을 읽어요." },
  { name: "patch_pricing_amounts", desc: "금액만 골라서 고쳐요. 안 적은 항목은 그대로 둬요.", write: true },
  {
    name: "upsert_pricing_preset",
    desc: "예시 프리셋 하나를 추가·교체해요. 나머지는 그대로 둬요.",
    write: true,
  },
  { name: "delete_pricing_preset", desc: "예시 프리셋 하나를 지워요.", write: true },
  { name: "replace_pricing_config", desc: "단가표를 통째로 교체해요. 항목 추가·삭제·순서 변경용이에요.", write: true },
  { name: "rollback_pricing_config", desc: "예전 버전 내용을 새 버전으로 다시 발행해요.", write: true },
];

const MCP_JSON = `{
  "mcpServers": {
    "dchain-pricing": {
      "command": "uv",
      "args": ["--directory", "apps/mcp", "run", "python", "-m", "mcp_pricing"],
      "env": {
        "DCHAIN_CONNECT_WEBSITE_MCP_API_BASE_URL": "http://localhost:8000",
        "DCHAIN_CONNECT_WEBSITE_MCP_ADMIN_EMAIL": "관리자 이메일",
        "DCHAIN_CONNECT_WEBSITE_MCP_ADMIN_PASSWORD": "관리자 비밀번호"
      }
    }
  }
}`;

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-md bg-background-alternative px-4 py-3 wds-caption-1 text-label-neutral">
      <code className="font-mono">{children}</code>
    </pre>
  );
}

export function McpGuide() {
  return (
    <details className="rounded-lg bg-background-elevated px-5 py-4 shadow-normal">
      <summary className="wds-headline-2 flex cursor-pointer items-center gap-2 text-label-strong">
        <Icon name="sparkle" size={18} className="text-primary" />
        MCP 로 단가 고치기
      </summary>

      <div className="mt-4 flex flex-col gap-5">
        <p className="wds-body-2-reading text-label-neutral">
          이 화면에서 하는 일을 Claude 같은 MCP 클라이언트에서 대화로도 할 수 있어요. 어드민 UI 와 같은 API 를
          쓰기 때문에 바꾼 내용은 똑같이 버전으로 남고, 이력 탭에서 되돌릴 수 있어요.
        </p>

        <div className="flex flex-col gap-2">
          <h3 className="wds-label-1 font-semibold text-label-strong">1. 연결하기</h3>
          <p className="wds-caption-1 text-label-neutral">
            저장소 루트의 <code className="font-mono">.mcp.json</code> 에 아래 블록을 넣어요. 이 파일은 git 에
            올라가지 않으니 비밀번호를 그대로 적어도 돼요.
          </p>
          <Code>{MCP_JSON}</Code>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="wds-label-1 font-semibold text-label-strong">2. 쓸 수 있는 도구</h3>
          <ul className="flex flex-col gap-1.5">
            {TOOLS.map((tool) => (
              <li key={tool.name} className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
                <span className="wds-caption-1 font-mono font-semibold text-label-strong sm:w-56 sm:shrink-0">
                  {tool.name}
                  {tool.write && <span className="ml-1 font-sans text-status-cautionary">쓰기</span>}
                </span>
                <span className="wds-caption-1 text-label-neutral">{tool.desc}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="wds-label-1 font-semibold text-label-strong">3. 이렇게 시켜요</h3>
          <ul className="flex list-inside list-disc flex-col gap-1">
            <li className="wds-caption-1 text-label-neutral">지금 단가표 보여줘</li>
            <li className="wds-caption-1 text-label-neutral">결제 200만원, 관리자 250만원으로 바꿔줘</li>
            <li className="wds-caption-1 text-label-neutral">앱 동반 개발 700만원으로 올리면 뭐가 바뀌는지만 보여줘</li>
            <li className="wds-caption-1 text-label-neutral">방금 발행한 거 되돌려줘</li>
          </ul>
        </div>

        <div className="flex items-start gap-2 rounded-md bg-background-alternative px-4 py-3">
          <Icon name="circleInfo" size={16} className="mt-0.5 shrink-0 text-label-assistive" />
          <p className="wds-caption-1 leading-relaxed text-label-neutral">
            금액만 고칠 때는 <code className="font-mono">patch_pricing_amounts</code> 를 쓰세요. 적지 않은 항목은
            그대로 두기 때문에 안전해요. <code className="font-mono">replace_pricing_config</code> 는 body 에 없는
            항목을 지우니, 항목을 추가·삭제할 때만 쓰거나 이 화면에서 직접 하세요. 발행 전에 결과만 보고 싶으면
            <code className="font-mono"> dry_run</code> 을 켜면 돼요.
          </p>
        </div>
      </div>
    </details>
  );
}

export default McpGuide;
